<template>
  <PForm
    ref="pForm"
    :show-header="false"
    :primary-cta="{}"
    :secondary-cta="{}"
    body-class="q-px-2x-md q-pt-xl"
    @submit.prevent="onSubmit"
  >
    <div class="col-12">
      <div class="flex justify-between">
        <span class="text-md text-weight-medium text-neutral">
          Bank Details
        </span>
        <div v-if="formState.edit">
          <PBtn
            dense
            flat
            fluid
            class="flex q-ml-auto"
            :disable="
              banks.length >= 5 || user.application_status == 'rejected'
            "
            @click="addBank"
          >
            <AddIcon class="icon--sm q-mr-sm" />
            <span>Add Bank</span>
          </PBtn>
        </div>
      </div>
      <div v-if="formState.edit">
        <PBtn dense flat fluid @click="openBankTimelines">
          <span> View History </span>
        </PBtn>
      </div>
      <q-separator class="q-mb-sm" />
    </div>
    <div v-if="!formState.edit" class="row q-col-gutter-x-xl q-col-gutter-y-sm">
      <div class="col-6">
        <PInput
          v-model.trim="formData.account_number"
          debounce="500"
          label="Bank Account Number"
          placeholder="Enter bank account number"
          required
          :disable="formState.edit"
          @update:model-value="onFieldUpdate('account_number')"
        ></PInput>
      </div>
      <div class="col-6">
        <PInput
          v-model.trim="formData.ifsc_code"
          debounce="300"
          label="Bank IFSC Code"
          placeholder="Enter IFSC code"
          required
          :validations="{
            length: 11,
            required: true,
            type: 'ifsc',
          }"
          :disable="formState.edit"
          @update:model-value="onFieldUpdate('ifsc_code')"
        ></PInput>
      </div>
      <div class="col-6">
        <PSelectInput
          v-model="formData.bank_name"
          :options="unref(bankFilter.entities)"
          label="Bank Name"
          option-label="bank_name"
          placeholder="Select Bank"
          required
          :disable="formState.edit"
          @filter="bankFilter.onFilter"
          @filter-abort="bankFilter.onFilterAbort"
          @update:model-value="onBankUpdate"
        />
      </div>
      <div class="col-6">
        <PInput
          v-model="formData.bank_beneficiary_name"
          label="Beneficiary Name"
          placeholder="Enter beneficiary name"
          required
          :disable="formState.edit"
          :loading="bankVerificationLoading"
          :error="!!bankVerificationError"
          @update:model-value="onFieldUpdate('bank_beneficiary_name')"
        ></PInput>
      </div>
      <div class="col-12">
        <PInput
          v-model.trim="formData.branch_address"
          label="Branch"
          placeholder="Enter branch address"
          type="textarea"
          required
          autogrow
          :error="!!ifscApiError"
          :error-message="ifscApiError"
          @update:model-value="onFieldUpdate('branch_address')"
        />
      </div>
      <div class="col-12">
        <PFile
          ref="fileComponent"
          v-model="fileModelValue"
          variant="compact"
          :label="
            documentData.name
              ? useChangeCase(documentData.name || '', 'capitalCase').value
              : ''
          "
          class="q-mt-sm"
          accept=".pdf, .jpeg, .jpg, .png"
          :required="documentData?.required"
          help="Primary bank account cancelled cheque"
          help-classes="text-xs text-neutral-80 q-mt-xs"
          @view-file="viewFile"
        >
          <template #action-icon>
            <UploadIcon class="icon--md" />
          </template>
        </PFile>
      </div>
    </div>
    <div v-else class="row q-col-gutter-x-xl q-col-gutter-y-sm">
      <BankList :banks="banks" />
    </div>
  </PForm>
</template>

<script setup>
import { useQuasar } from "quasar";
import PConfirmDialog from "src/components/PConfirmDialog.vue";
import * as bankApi from "src/api/bank";
import bankDetailsForm from "../models/bankDetailsForm";
import AddIcon from "assets/icons/add.svg";
import UploadIcon from "assets/icons/re-upload.svg";
import { externalBanks } from "src/api";
import { useChangeCase } from "@vueuse/integrations/useChangeCase";
import { ifsc as ifscCode } from "src/assets/scripts/utilities/validationRules";

import { useBankVerification } from "src/modules/user/composables/bankVerification";

const $q = useQuasar();

const pForm = ref(null);
const bankFilter = ref({});
const bankDetailsLoading = ref(false);
const { formState, user } = storeToRefs(useUserStore());
const { banks } = storeToRefs(useBankStore());
const { putUser, toggleUserForm, getDocumentDetails } = useUserStore();
const { toggleAddBankForm, getBanks } = useBankStore();
const { toggleTimelinesDrawer } = useAuditLogsStore();
const { viewFile } = useFile();
const onGoingOnboarding = inject("onGoingOnboarding");
const documentData = ref({});
const trackFormChanges = ref(true);
const ifscApiError = ref("");
const ifscApiErrorMsg = ref(
  "Unable to fetch branch address. Please enter the details manually.",
);

const computedBanksData = computed(() => {
  return unref(banks)?.[0];
});

const { formData, submitForm } = useForm({
  formModel: bankDetailsForm(),
  entity: computedBanksData,
  formRef: pForm,
  isForm: formState,
  put: putUser,
  toggleForm: toggleUserForm,
  editMode: unref(onGoingOnboarding),
});

const { verifyBankAccount, bankVerificationLoading, bankVerificationError } =
  useBankVerification(formData);

const fetchBankDetails = async () => {
  const ifsc = unref(formData).ifsc_code;

  try {
    ifscApiError.value = "";

    if (!ifsc) {
      unref(formData).bank_name = "";

      unref(formData).branch_address = "";

      return;
    }

    if (ifsc.length !== 11 || !ifscCode(ifsc)) {
      unref(formData).bank_name = "";

      unref(formData).branch_address = "";

      ifscApiError.value = ifscApiErrorMsg.value;

      return;
    }

    bankDetailsLoading.value = true;

    const response = await bankApi.fetchBankDetailsFromIFSC(ifsc);
    const findBank = unref(bankFilter)?.entities?.filter(
      (bank) => bank.bank_name === response.data.BANK,
    );

    if (findBank?.length) {
      unref(formData).bank_name = unref(findBank)?.[0]?.bank_name;

      unref(formData).branch_address = response.data.ADDRESS || "";

      if (!unref(formData).branch_address) {
        ifscApiError.value = ifscApiErrorMsg.value;
      }

      unref(formData).code = unref(findBank)[0].code;
    }
  } catch (error) {
    console.error(error);

    unref(formData).bank_name = "";

    unref(formData).branch_address = "";

    ifscApiError.value = ifscApiErrorMsg.value;
  } finally {
    bankDetailsLoading.value = false;
  }
};

const initBankFilter = async () => {
  const externalBank = await externalBanks.index();

  bankFilter.value = useFilter({
    entities: externalBank.data?.data,
    entity: "externalBanks",
    props: ["bank_name"],
  });
};

const addBank = () => {
  toggleAddBankForm({
    open: true,
    level: 0,
    props: {
      bank: {},
    },
  });
};

const validateCheque = () => {
  let isValidated = true;

  if (!unref(formData).file || Object.keys(unref(formData).file).length === 0) {
    isValidated = false;
  }

  return isValidated;
};

defineExpose({
  pForm,
  validateCheque,
});

const onSubmit = async () => {
  $q.dialog({
    component: PConfirmDialog,
    componentProps: {
      text: `Are you sure you want to edit these details ?`,
      isFooterVisible: true,
      submittable: true,
      cancellable: true,
      closable: true,
      onConfirm: async () => submitForm(),
    },
  });
};

const openBankTimelines = () => {
  toggleTimelinesDrawer({
    open: true,
    level: 0,
    props: {
      entityId: unref(user)?._id,
      entityName: "UserBanks",
      heading: "Bank History Logs",
      showActor: true,
      detailFields: [
        { key: "bank_name", case: "capitalCase" },
        { key: "bank_beneficiary_name", case: "capitalCase" },
        { key: "ifsc_code", case: "constantCase" },
        { key: "account_number", case: "constantCase" },
      ],
    },
  });
};

watch(
  () => unref(banks),
  (newBanks) => {
    if (newBanks?.length) {
      const primaryBank = newBanks[0];

      formData.value = {
        ...formData.value,
        ...primaryBank,
        file_id: primaryBank?.file?._id,
        _id: primaryBank?._id,
      };
    }
  },
  { immediate: true, deep: true },
);

const fileComponent = ref(null);

const fileModelValue = computed({
  get() {
    return formData.value.file || null;
  },
  set(value) {
    formData.value.file = value || null;
  },
});

const onFieldUpdate = (field) => {
  if (field === "ifsc_code") {
    fetchBankDetails();
  }

  if (field === "account_number" || field === "ifsc_code") {
    verifyBankAccount();
  }

  if (field === "bank_beneficiary_name") {
    bankVerificationError.value = "";
  }

  if (field === "branch_address") {
    ifscApiError.value = "";
  }

  if (unref(formData).file) {
    unref(formData).file = null;
  }

  trackFormChanges.value = false;
};

const onBankUpdate = (bank) => {
  const { bank_name = "", code = "" } = bank;

  formData.value.bank_name = bank_name;

  formData.value.code = code;

  onFieldUpdate();
};

const getUploadDocumentDetails = async () => {
  const code = "cancelled_cheque";
  const data = await getDocumentDetails(code);

  documentData.value = data?.data?.data[0];
};

onBeforeMount(async () => {
  await getUploadDocumentDetails();

  await getBanks();
});

watchEffect(() => {
  initBankFilter();
});

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => {
      return {};
    },
  },
});

const emit = defineEmits(["update:modelValue"]);

const computedFormData = computed({
  get: function () {
    return props.modelValue;
  },
  set: function (updatedFormData) {
    emit("update:modelValue", updatedFormData);
  },
});

watch(
  formData,
  (bank) => {
    computedFormData.value = bank;
  },
  {
    immediate: true,
  },
);

watch(
  () => unref(formData).file,
  () => {
    unref(formData).file_category = unref(documentData).category;

    unref(formData).file_type = unref(documentData).code;

    unref(formData).shop_name = unref(user).basicInfo.shop_name;

    unref(formData).file_id = unref(banks)?.[0]?.file?._id;

    unref(formData).document_id = unref(documentData)._id;

    unref(formData)._id = unref(banks)?.[0]?._id;
  },
);
</script>
